"use client"

import { useCallback } from "react"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Lock, StickyNote, Users, Pin } from "lucide-react"
import { useLocalizedField } from "@/utils/language-utils"
import { useTranslation } from "@/hooks/use-translation"
import { useViewManager } from "../view-manager-context"

// ─── Sortable Item ───────────────────────────────────────────────────

function SortableFieldItem({ id, field, isFixed, isSelected, onSelect }: {
  id: string
  field: any
  isFixed?: boolean
  isSelected?: boolean
  onSelect: (id: string) => void
}) {
  const getLocalizedField = useLocalizedField()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: isFixed })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  }

  const title = getLocalizedField(field, 'columnName') || field.columnCode || id

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer
        ${isSelected
          ? 'bg-blue-50 border-blue-300 shadow-sm'
          : isDragging
            ? 'bg-white border-blue-400 shadow-md'
            : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50'
        }
        ${isFixed ? 'opacity-70' : ''}
      `}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(id) } }}
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
    >
      {/* Drag handle */}
      {!isFixed ? (
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical size={14} />
        </div>
      ) : (
        <div className="flex-shrink-0 text-gray-300">
          <Pin size={14} />
        </div>
      )}

      {/* Field name */}
      <span className="text-sm text-gray-800 font-medium truncate flex-1">{title}</span>

      {/* Property indicators */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {field.isPrivate && <Lock size={11} className="text-gray-400" />}
        {field.displayNote && <StickyNote size={11} className="text-gray-400" />}
        {field.isEnabledForClients && <Users size={11} className="text-amber-500" />}
        {field.columnTypeCode && (
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1 py-0.5 rounded">
            {field.columnTypeCode}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Column Reorder List ─────────────────────────────────────────────

interface ColumnReorderListProps {
  selectedFieldId?: string | null
  onFieldSelect: (fieldId: string) => void
  /** When provided, only show items matching these IDs (search filter) */
  filterIds?: string[]
}

export function ColumnReorderList({ selectedFieldId, onFieldSelect, filterIds }: ColumnReorderListProps) {
  const { t } = useTranslation()
  const { state, dispatch } = useViewManager()

  const stageId = state.activeEditStage
  const allColumnIds = state.draftStageColumns[stageId] || []
  const columnIds = filterIds ? allColumnIds.filter(id => filterIds.includes(id)) : allColumnIds
  const fieldsInUse = state.originalConfig?.fieldsInUse || []
  const stageConfig = (state.originalConfig?.stageConfigurations || []).find(
    (s: any) => (s.stageId || s.stageCode) === stageId
  )
  const stageFields = stageConfig?.fieldConfigurations || []

  // Build field map
  const fieldMap = new Map<string, any>()
  for (const f of fieldsInUse) fieldMap.set(f.fieldConfigurationId, f)
  for (const f of stageFields) fieldMap.set(f.fieldConfigurationId, f)

  // Identify fixed columns (Message always first)
  const messageFieldId = columnIds.find(id => {
    const f = fieldMap.get(id)
    return f?.columnCode === 'Message' || f?.columnCode === 'message'
  })
  const fixedIds = messageFieldId ? [messageFieldId] : []
  const reorderableIds = columnIds.filter(id => id !== messageFieldId)

  const isFiltered = !!filterIds
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (isFiltered) return // Don't reorder when search filter is active
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = reorderableIds.indexOf(active.id as string)
    const newIndex = reorderableIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const newReorderable = arrayMove(reorderableIds, oldIndex, newIndex)
    dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: [...fixedIds, ...newReorderable] } })
  }, [isFiltered, reorderableIds, fixedIds, stageId, dispatch])

  if (columnIds.length === 0) {
    return (
      <div className="text-sm text-gray-400 text-center py-8">
        {t('vmNoVisibleColumns' as any) || 'No visible columns for this stage'}
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="space-y-1">
        {/* Fixed columns */}
        {fixedIds.map(id => {
          const field = fieldMap.get(id) || { columnCode: id }
          return (
            <SortableFieldItem
              key={id}
              id={id}
              field={field}
              isFixed={true}
              isSelected={selectedFieldId === id}
              onSelect={onFieldSelect}
            />
          )
        })}
        {/* Reorderable columns */}
        <SortableContext items={reorderableIds} strategy={verticalListSortingStrategy}>
          {reorderableIds.map(id => {
            const field = fieldMap.get(id) || { columnCode: id }
            return (
              <SortableFieldItem
                key={id}
                id={id}
                field={field}
                isSelected={selectedFieldId === id}
                onSelect={onFieldSelect}
              />
            )
          })}
        </SortableContext>
      </div>
    </DndContext>
  )
}
